import { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react'
import { Sparkles, X, ArrowUp, Shield, ExternalLink } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import AgentSteps from './AgentSteps'
import ResponseCard from './ResponseCard'
import SuggestionChips from './SuggestionChips'
import { readSSE } from '../../utils/sse'

const CONTEXT_ID = 'cdg31'
const MAX_QUESTION_LENGTH = 4000
const POC_API_URL = import.meta.env.VITE_CDG31_API_URL || ''

const SUGGESTIONS = [
  "Comment m'inscrire a un concours ?",
  "Je recrute un redacteur territorial",
  "Prochaines formations management",
  "Rupture conventionnelle : comment ca marche ?",
]

function SourcesList({ sources }) {
  if (!sources?.length) return null
  return (
    <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid var(--border-color, #e5e5e5)' }}>
      <div style={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--fg-faint, #999)', marginBottom: '0.25rem' }}>
        Sources cdg31.fr
      </div>
      {sources.map((s, i) => (
        <a key={i} href={s.url} target="_blank" rel="noopener noreferrer"
          style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.72rem', color: 'var(--cdg-blue, #1B3B6F)', padding: '0.15rem 0', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <ExternalLink size={10} style={{ flexShrink: 0 }} />
          {s.title || s.url}
        </a>
      ))}
    </div>
  )
}

const SUGGESTIONS_RE = /^SUGGESTIONS:\s*(.+)/m
function stripSuggestions(text) { return text.replace(SUGGESTIONS_RE, '').trimEnd() }

const markdownComponents = {
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
  ),
}

const Cdg31Chat = forwardRef(function Cdg31Chat({ open, onClose, profile }, ref) {
  const [messages, setMessages] = useState([])
  const [draft, setDraft] = useState('')
  const [loading, setLoading] = useState(false)
  const [searchStatus, setSearchStatus] = useState(null)
  const [sessionId, setSessionId] = useState(null)
  const [agentSteps, setAgentSteps] = useState([])
  const [dynamicSuggestions, setDynamicSuggestions] = useState([])
  const bodyRef = useRef(null)
  const abortRef = useRef(null)

  const usePocBackend = Boolean(POC_API_URL)

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight
  }, [messages, loading, searchStatus, agentSteps])

  // Affiche le message d'accueil immédiatement, sans appel réseau.
  // La session RagFlow sera initialisée de façon paresseuse au premier envoi.
  useEffect(() => {
    if (!open || sessionId || messages.length > 0) return
    const welcomeText = usePocBackend
      ? "Bonjour ! Je suis l'assistant du CDG31. Posez-moi une question sur les concours, le statut, les formations ou le recrutement — je recherche la reponse sur cdg31.fr en temps reel."
      : "Bonjour ! Je suis l'assistant du CDG31. Posez-moi une question sur les concours, le statut, les formations ou le recrutement — je vous oriente vers la bonne ressource."
    setMessages([{ role: 'bot', text: welcomeText }])
  }, [open, usePocBackend]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => abortRef.current?.abort()
  }, [])

  const sendViaPoc = useCallback(async (trimmed) => {
    setMessages(m => [...m, { role: 'user', text: trimmed }])
    setDraft('')
    setLoading(true)
    setSearchStatus(null)
    setAgentSteps([])
    setDynamicSuggestions([])
    abortRef.current = new AbortController()

    let answer = ''
    let sources = []
    let cards = []

    try {
      const historyPayload = messages
        .filter(m => m.role === 'user' || m.role === 'bot')
        .slice(-6)
        .map(m => ({ role: m.role === 'bot' ? 'assistant' : 'user', content: m.text }))

      const res = await fetch(`${POC_API_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: trimmed, history: historyPayload, session_id: sessionId, profile }),
        signal: abortRef.current.signal,
      })

      if (!res.ok) throw new Error(`HTTP ${res.status}`)

      for await (const evt of readSSE(res)) {
        switch (evt.type) {
          case 'agent_step':
            setAgentSteps(prev => {
              const idx = prev.findIndex(s => s.tool === evt.content.tool)
              if (idx >= 0) { const u = [...prev]; u[idx] = evt.content; return u }
              return [...prev, evt.content]
            })
            break
          case 'search_status':
            setSearchStatus(evt.content)
            break
          case 'sources':
            sources = evt.content || []
            setSearchStatus(null)
            break
          case 'card':
            cards.push(evt.content)
            break
          case 'token':
            answer += evt.content
            setMessages(m => {
              const last = m[m.length - 1]
              if (last?.role === 'bot' && last?._streaming) {
                return [...m.slice(0, -1), { ...last, text: answer }]
              }
              return [...m, { role: 'bot', text: answer, sources, cards: [...cards], _streaming: true }]
            })
            break
          case 'suggestions':
            setDynamicSuggestions(evt.content || [])
            break
          case 'error':
            setMessages(m => [...m, { role: 'bot', text: evt.content }])
            break
          case 'done':
            if (evt.session_id) setSessionId(evt.session_id)
            setMessages(m => {
              const last = m[m.length - 1]
              if (last?._streaming) {
                const { _streaming, ...clean } = last
                const cleanText = stripSuggestions(clean.text)
                return [...m.slice(0, -1), { ...clean, text: cleanText, sources: sources.length ? sources : clean.sources, cards: cards.length ? cards : clean.cards }]
              }
              if (!answer && cards.length > 0) return [...m, { role: 'bot', text: '', sources, cards }]
              if (!answer) return [...m, { role: 'bot', text: 'Aucune reponse generee. Essayez de reformuler votre question.' }]
              return m
            })
            break
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') return
      setMessages(m => [...m, { role: 'bot', text: "Erreur de connexion au service IA. Contactez le CDG31 au 05 81 91 93 00." }])
    } finally {
      setLoading(false)
      setSearchStatus(null)
    }
  }, [messages, sessionId, profile])

  const sendViaDrupal = useCallback(async (trimmed) => {
    setMessages(m => [...m, { role: 'user', text: trimmed }])
    setDraft('')
    setLoading(true)
    abortRef.current = new AbortController()

    try {
      const res = await fetch('/api/ai-chat/message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          context_id: CONTEXT_ID,
          question: trimmed,
          ...(sessionId ? { session_id: sessionId } : {}),
        }),
        signal: abortRef.current.signal,
      })
      const data = await res.json()
      if (res.ok && data.answer) {
        setMessages(m => [...m, { role: 'bot', text: data.answer, sources: data.reference }])
        if (data.session_id) setSessionId(data.session_id)
      } else {
        setMessages(m => [...m, { role: 'bot', text: "Desole, une erreur est survenue. Vous pouvez appeler le 05 81 91 93 00 ou utiliser notre formulaire de contact." }])
      }
    } catch (err) {
      if (err.name === 'AbortError') return
      setMessages(m => [...m, { role: 'bot', text: "Desole, une erreur est survenue. Vous pouvez appeler le 05 81 91 93 00 ou utiliser notre formulaire de contact." }])
    } finally {
      setLoading(false)
    }
  }, [sessionId])

  const send = useCallback(async (text) => {
    const trimmed = (text ?? draft).trim()
    if (!trimmed || loading) return
    if (trimmed.length > MAX_QUESTION_LENGTH) return
    if (usePocBackend) {
      await sendViaPoc(trimmed)
    } else {
      await sendViaDrupal(trimmed)
    }
  }, [draft, loading, usePocBackend, sendViaPoc, sendViaDrupal])

  useImperativeHandle(ref, () => ({ send }), [send])

  const showSuggestions = messages.length <= 1 && !loading

  return (
    <>
      {!open && (
        <button className="cdg-chat-fab" onClick={() => onClose(true)} aria-label="Ouvrir l'assistant">
          <Sparkles size={20} />
          <span>Assistant IA</span>
        </button>
      )}

      <div className={'cdg-chat-drawer' + (open ? ' open' : '')} aria-hidden={!open}>
        <div className="cdg-chat-head">
          <div className="cdg-chat-head-ic"><Sparkles size={22} /></div>
          <div className="cdg-chat-head-text">
            <strong>Assistant CDG31</strong>
            <span><span className="cdg-chat-pulse" />En ligne — reponse immediate</span>
          </div>
          <button className="cdg-chat-close" onClick={() => onClose(false)} aria-label="Fermer"><X size={18} /></button>
        </div>

        <div className="cdg-chat-body" ref={bodyRef}>
          {messages.map((m, i) => (
            <div key={i} className={'cdg-chat-msg ' + m.role}>
              {m.role === 'bot' && <div className="cdg-chat-ava"><Sparkles size={14} /></div>}
              <div className="cdg-chat-bubble">
                {m.role === 'bot' ? (
                  <>
                    {m.cards?.map((card, ci) => <ResponseCard key={ci} card={card} />)}
                    {m.text && (
                      <div className="cdg-md-content">
                        <ReactMarkdown components={markdownComponents}>{m.text}</ReactMarkdown>
                      </div>
                    )}
                  </>
                ) : m.text}
                {m.role === 'bot' && <SourcesList sources={m.sources} />}
              </div>
            </div>
          ))}
          {loading && agentSteps.length > 0 && (
            <div className="cdg-chat-msg bot">
              <div className="cdg-chat-ava"><Sparkles size={14} /></div>
              <div className="cdg-chat-bubble">
                <AgentSteps steps={agentSteps} collapsed={false} />
              </div>
            </div>
          )}
          {searchStatus && (
            <div className="cdg-chat-msg bot">
              <div className="cdg-chat-ava"><Sparkles size={14} /></div>
              <div className="cdg-chat-bubble" style={{ fontStyle: 'italic', fontSize: '0.8rem', color: 'var(--fg-muted, #666)' }}>
                {searchStatus}
              </div>
            </div>
          )}
          {loading && agentSteps.length === 0 && !searchStatus && (
            <div className="cdg-chat-msg bot">
              <div className="cdg-chat-ava"><Sparkles size={14} /></div>
              <div className="cdg-chat-bubble cdg-chat-typing">
                <span /><span /><span />
              </div>
            </div>
          )}
          {showSuggestions && dynamicSuggestions.length === 0 && (
            <div className="cdg-chat-sugg">
              <div className="cdg-chat-sugg-label">Questions frequentes</div>
              {SUGGESTIONS.map(s => (
                <button key={s} onClick={() => send(s)} className="cdg-chat-sugg-btn">{s}</button>
              ))}
            </div>
          )}
          {!loading && dynamicSuggestions.length > 0 && (
            <SuggestionChips suggestions={dynamicSuggestions} onSelect={send} />
          )}
        </div>

        <form className="cdg-chat-input" onSubmit={(e) => { e.preventDefault(); send() }}>
          <input
            type="text"
            placeholder="Posez votre question..."
            value={draft}
            onChange={e => setDraft(e.target.value)}
            disabled={loading}
          />
          <button type="submit" disabled={loading || !draft.trim()} aria-label="Envoyer">
            <ArrowUp size={18} />
          </button>
        </form>
        <div className="cdg-chat-foot">
          <Shield size={11} />
          Les informations echangees restent confidentielles. Pour les dossiers personnels, privilegiez votre espace connecte.
        </div>
      </div>
    </>
  )
})

export default Cdg31Chat
